import { useMemo } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { AuditTimelineBucket } from "../../api/audit-logs"

type TimelineInterval = "hour" | "day" | "week"

interface TimelineChartProps {
  buckets: AuditTimelineBucket[]
  isLoading: boolean
  interval: TimelineInterval
  onIntervalChange: (interval: TimelineInterval) => void
}

const CHART_COLORS = [
  "#786FF6",
  "#63C7C4",
  "#F59E0B",
  "#EF4444",
  "#10B981",
  "#6366F1",
  "#EC4899",
  "#8B5CF6",
  "#14B8A6",
  "#F97316",
  "#06B6D4",
  "#84CC16",
  "#E11D48",
  "#7C3AED"
]

const INTERVALS: { value: TimelineInterval; label: string }[] = [
  { value: "hour", label: "Hour" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" }
]

const TimelineChart = ({
  buckets,
  isLoading,
  interval,
  onIntervalChange
}: TimelineChartProps) => {
  const chartOptions = useMemo((): Highcharts.Options => {
    const eventTypes = new Set<string>()
    buckets.forEach(b => {
      Object.keys(b.events).forEach(et => eventTypes.add(et))
    })

    const sortedEventTypes = Array.from(eventTypes).sort()

    const series: Highcharts.SeriesColumnOptions[] = sortedEventTypes.map(
      (et, i) => ({
        type: "column" as const,
        name: et.replace(/_/g, " "),
        color: CHART_COLORS[i % CHART_COLORS.length],
        data: buckets.map(b => [b.timestamp * 1000, b.events[et] || 0])
      })
    )

    return {
      chart: { type: "column", height: 260 },
      title: { text: "" },
      credits: { enabled: false },
      xAxis: {
        type: "datetime",
        labels: { style: { color: "#A3A3A9" } }
      },
      yAxis: {
        min: 0,
        title: { text: "Events" },
        stackLabels: { enabled: false }
      },
      legend: {
        enabled: true,
        align: "left",
        itemStyle: { fontWeight: "normal", fontSize: "11px" }
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderWidth: 0,
          borderRadius: 2
        }
      },
      tooltip: {
        backgroundColor: "#42405A",
        borderColor: "#42405A",
        borderRadius: 5,
        shadow: false,
        style: { color: "#ffffff", fontSize: "12px", padding: "10px" },
        shared: true,
        xDateFormat: "%b %e, %Y %H:%M"
      },
      series
    }
  }, [buckets])

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-neutral-200 rounded w-32" />
          <div className="h-[220px] bg-neutral-100 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold text-neutral-800">
          Event timeline
        </h3>
        <div className="flex rounded-md border border-neutral-200 overflow-hidden">
          {INTERVALS.map(item => (
            <button
              key={item.value}
              onClick={() => onIntervalChange(item.value)}
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                interval === item.value
                  ? "bg-primary-400 text-white"
                  : "bg-white text-neutral-600 hover:bg-neutral-50"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  )
}

export default TimelineChart
